import React, { useState, useContext, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Breadcrumb,
  Spinner,
  ListGroup,
  Tabs,
  Tab,
} from "react-bootstrap";
import { canSetPersonalBranding } from "../helper/BusinessLogic";
import { UserInfo } from "../helper/Context";
import { Link } from "react-router-dom";
import { Formik } from "formik";
import { APIResetPassword, APIUpdateUserInfo } from "../helper/APIFunctions";
import BrandingForm from "../components/BrandingForm";
import CurrentBillingDetails from "../components/CurrentBillingDetails";
import { useNavigate } from "react-router-dom";
import {
  getVisualLifetimeHrs,
  getVisualLimit,
  getUseTypes,
} from "../helper/BusinessLogic";
import { contactUs, shallowCopy } from "../helper/Utils";
import * as Yup from "yup";
import './SettingsPage.css';
import './Tabs.css';
import {
  getUserSettings,
  saveUserSettings,
  getDefaultUserSettings
} from "../helper/UserSettings";

const formatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "numeric",
  minute: "2-digit",
});

const passwordChangeSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .required("Required")
    .min(8, "Too short")
    .max(72, "Too long"),
  newPassword: Yup.string()
    .required("Required")
    .min(8, "Password must be at least 8 characters")
    .max(72, "Password cannot be have more than 72 characters"),
  newPasswordConfirm: Yup.string()
    .required("Required")
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
});

const PasswordChange = (props) => {
  const [result, setResult] = useState(null);
  return (
    <>
      {result ? <p className={result.style}>{result.message}</p> : null}
      <Formik
        initialValues={{
          currentPassword: "",
          newPassword: "",
          newPasswordConfirm: "",
        }}
        validationSchema={passwordChangeSchema}
        onSubmit={(values, actions) => {
          setResult(null);
          APIResetPassword(
            values.newPassword,
            values.newPasswordConfirm,
            (body) => {
              setResult({
                style: "text-success text-center",
                message: "Success!",
              });
              actions.setSubmitting(false);
            },
            (body) => {
              setResult({
                style: "text-danger text-center",
                message: body.toString(),
              });
              actions.setSubmitting(false);
            },
            null,
            values.currentPassword
          );
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
          isSubmitting,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="change-password-current" className="mb-3">
              <Form.Label>Current Password</Form.Label>
              <Form.Control
                type="password"
                name="currentPassword"
                autoComplete="current-password"
                value={values.currentPassword}
                onBlur={handleBlur}
                onChange={handleChange}
                disabled={isSubmitting}
                isInvalid={touched.currentPassword && errors.currentPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.currentPassword}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="change-password-new" className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                name="newPassword"
                autoComplete="new-password"
                value={values.newPassword}
                onBlur={handleBlur}
                onChange={handleChange}
                disabled={isSubmitting}
                isInvalid={touched.newPassword && errors.newPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.newPassword}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group
              controlId="change-password-new-confirm"
              className="mb-3"
            >
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                autoComplete="new-password"
                name="newPasswordConfirm"
                value={values.newPasswordConfirm}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                isInvalid={
                  touched.newPasswordConfirm && errors.newPasswordConfirm
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors.newPasswordConfirm}
              </Form.Control.Feedback>
            </Form.Group>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="resetting password"
                  aria-hidden="true"
                />
              ) : (
                <>Reset Password</>
              )}
            </Button>
          </Form>
        )}
      </Formik>
    </>
  );
};

const getCountText = (tier) => {
  const count = getVisualLimit(tier);
  return count ? `${count} visual limit` : "Unlimited visuals";
};

const getLifetimeText = (tier) => {
  const hrs = getVisualLifetimeHrs(tier);
  return hrs
    ? `${Math.round(hrs / 24)} day visual lifetime`
    : "Infinite lifetime";
};

const ContentPreferences = ({ userInfo }) => {
  const [userSettings, setUserSettings] = useState(null);
  const [missionStatement, setMissionStatement] = useState('');
  const [institutionProfile, setInstitutionProfile] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    if (!userInfo || !userInfo.email) return;

    let settings = getUserSettings(userInfo.email);
    if (!settings) {
      settings = getDefaultUserSettings(userInfo);
      saveUserSettings(userInfo.email, settings);
    }
    setUserSettings(settings);
    setMissionStatement(settings.missionStatement || '');
    setInstitutionProfile(settings.institutionProfile || '');
  }, [userInfo]);

  const handleSave = () => {
    if (!userInfo || !userInfo.email) return;

    setIsSaving(true);
    setSaveMessage(null);
    APIUpdateUserInfo({ missionStatement, institutionProfile }, () => {
      const updatedSettings = {
        ...userSettings,
        missionStatement,
        institutionProfile
      };

      const saved = saveUserSettings(userInfo.email, updatedSettings);

      if (saved) {
        setUserSettings(saved);
        setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
      } else {
        setSaveMessage({ type: 'danger', text: 'Failed to save settings. Please try again.' });
      }

      setIsSaving(false);

      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    }, () => {
      setIsSaving(false);
      setSaveMessage({ type: 'danger', text: 'Failed to save settings. Please try again.' });
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    });
  };

  if (!userSettings) {
    return (
      <div className="text-white mt-4 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="text-white mt-4">
      <div className="settings-card">
        <h4>Mission Statement</h4>
        <p>
          This helps AI generate content aligned with your goals and values. Used when creating scrollytelling stories and other AI-assisted content.
        </p>
        <Form.Group className="mb-3">
          <Form.Control
            as="textarea"
            rows={4}
            maxLength={500}
            value={missionStatement}
            onChange={(e) => setMissionStatement(e.target.value)}
            placeholder="Enter your mission statement or organizational goals..."
          />
          <Form.Text className="text-muted">
            {missionStatement.length}/500 characters
          </Form.Text>
        </Form.Group>
      </div>

      <div className="settings-card">
        <h4>Institution Profile</h4>
        <p>
          Describe your institution or organization. This context helps AI tailor content suggestions to your specific field and audience.
        </p>
        <Form.Group className="mb-3">
          <Form.Control
            as="textarea"
            rows={4}
            maxLength={500}
            value={institutionProfile}
            onChange={(e) => setInstitutionProfile(e.target.value)}
            placeholder="Describe your institution, research focus, or area of expertise..."
          />
          <Form.Text className="text-muted">
            {institutionProfile.length}/500 characters
          </Form.Text>
        </Form.Group>
      </div>

      <div className="d-flex align-items-center">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Saving...
            </>
          ) : (
            'Save Content Preferences'
          )}
        </Button>
        {saveMessage && (
          <span className={`ms-3 text-${saveMessage.type}`}>
            {saveMessage.text}
          </span>
        )}
      </div>
    </div>
  );
};

const AccountInfoPage = (props) => {
  let navigate = useNavigate();
  const [newUseType, setNewUseType] = useState(
    props.userInfo.use_type || "other"
  );
  const [updatingInfo, setUpdatingInfo] = useState(false);
  return (
    <div className="text-white mt-4">
      <div className="settings-card">
        <h4>Account Details</h4>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm="3">Email:</Form.Label>
          <Col sm="9">
            <Form.Control readOnly defaultValue={props.userInfo.email} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm="3">Plan:</Form.Label>
          <Col sm="9">
            <Form.Control readOnly defaultValue={`${props.userInfo.tier.charAt(0).toUpperCase() + props.userInfo.tier.slice(1)} ${props.userInfo.tier !== "free" ? `(${props.userInfo.billing_period === "month" ? "Monthly" : "Annually"})` : ""}`} />
            {props.userInfo.free_trial_ends && props.userInfo.free_trial_ends * 1000 > Date.now() && (
              <Form.Text className="text-muted">
                Your free trial period ends {formatter.format(props.userInfo.free_trial_ends * 1000)}
              </Form.Text>
            )}
            {
              props.userInfo.pending_change && (
                <Form.Text className="text-muted">
                  Your plan will be updated to {props.userInfo.pending_change.new_tier.charAt(0).toUpperCase() + props.userInfo.pending_change.new_tier.slice(1)} {props.userInfo.pending_change.new_tier !== "free" ? `(${props.userInfo.pending_change.billing_period === "month" ? "Monthly" : " Annually"})` : ""} on {formatter.format(props.userInfo.pending_change.timestamp * 1000)}
                </Form.Text>
              )
            }
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Col sm={{ span: 9, offset: 3 }}>
            {props.userInfo.tier == "free" ? (
              <Button variant="primary" onClick={() => navigate("/pricing")}>Upgrade for permanent links and more storage</Button>
            ) : props.userInfo.has_stripe ? (
              <Button variant="primary" onClick={() => navigate("/pricing")}>Manage your subscription</Button>
            ) : (
              <Button variant="primary" onClick={() => navigate("/contact")}>Contact us to manage your plan</Button>
            )}
          </Col>
        </Form.Group>
      </div>

      <div className="settings-card">
        <h4>Plan Details</h4>
        <ListGroup variant="flush">
          {props.userInfo.tier === 'free' && (
            <>
              <ListGroup.Item className="bg-transparent text-white">Standard image formats</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">Simple molecule viewer</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">{getCountText("free")}</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">{getLifetimeText("free")}</ListGroup.Item>
            </>
          )}
          {props.userInfo.tier === 'premium' && (
            <>
              <ListGroup.Item className="bg-transparent text-white">Standard image formats</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">Clinical image formats</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">Simple molecule viewer</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">{getCountText("premium")}</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">{getLifetimeText("premium")}</ListGroup.Item>
            </>
          )}
          {props.userInfo.tier === 'unlimited' && (
            <>
              <ListGroup.Item className="bg-transparent text-white">Standard image formats</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">Clinical imaging formats</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">Simple molecule viewer</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">{getCountText("unlimited")}</ListGroup.Item>
              <ListGroup.Item className="bg-transparent text-white">{getLifetimeText("unlimited")}</ListGroup.Item>
            </>
          )}
        </ListGroup>
        {props.userInfo.organization && (
          <Form.Group as={Row} className="mt-3">
            <Form.Label column sm="3">Group:</Form.Label>
            <Col sm="9">
              <Form.Control readOnly defaultValue={props.userInfo.organization.name} />
            </Col>
          </Form.Group>
        )}
      </div>
      {props.userInfo.oauth_created ? (
        <div className="settings-card">
          <h4>External Login</h4>
          <p>You are logged in with Google. Please contact support if you wish to disconnect your account.</p>
        </div>
      ) : (
        <div className="settings-card">
          <h4>Change Password</h4>
          <PasswordChange />
        </div>
      )}
      <div className="settings-card">
        <h4>I am a...</h4>
        <Form.Group as={Row} className="mb-3 align-items-center">
          <Col sm="9">
            <Form.Select
              name="useType"
              value={newUseType}
              onChange={(e) => setNewUseType(e.target.value)}
              style={{ maxWidth: '300px' }}
            >
              {getUseTypes().map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col sm="3">
            <Button
              variant="primary"
              disabled={updatingInfo || props.userInfo.use_type === newUseType}
              onClick={() => {
                setUpdatingInfo(true);
                APIUpdateUserInfo(
                  { useType: newUseType },
                  () => {
                    const t = shallowCopy(props.userInfo);
                    t.use_type = newUseType;
                    props.setUserInfo(t);
                    setUpdatingInfo(false);
                  },
                  () => {
                    setUpdatingInfo(false);
                  }
                );
              }}
            >
              Update
            </Button>
          </Col>
        </Form.Group>
      </div>
    </div>
  );
};

const SettingsPage = (props) => {
  return (
    <div className="profile-page-container">
      <h1 className="gradient-text text-center mb-4">Account Settings</h1>
      <Container style={{ maxWidth: '800px' }}>
        <UserInfo.Consumer>
          {({ userInfo, setUserInfo }) => {
            return canSetPersonalBranding(userInfo.tier) ||
              userInfo.has_default_payment_method ? (
              <Tabs
                defaultActiveKey="info"
                id="account-settings-tabs"
                mountOnEnter={userInfo.has_default_payment_method}
                className="mb-3"
                variant="tabs"
                fill
              >
                <Tab eventKey="info" title="Account Info">
                  <AccountInfoPage
                    userInfo={userInfo}
                    setUserInfo={setUserInfo}
                  />
                </Tab>
                <Tab eventKey="content" title="Content Preferences">
                  <ContentPreferences userInfo={userInfo} />
                </Tab>
                {canSetPersonalBranding(userInfo.tier) ? (
                  <Tab eventKey="branding" title="Personal Branding">
                    <BrandingForm isOrg={false} />
                  </Tab>
                ) : null}
                {userInfo.has_default_payment_method ? (
                  <Tab eventKey="billing" title="Saved Payment">
                    <CurrentBillingDetails />
                  </Tab>
                ) : null}
              </Tabs>
            ) : (
              <Tabs
                defaultActiveKey="info"
                id="account-settings-tabs"
                className="mb-3"
                variant="tabs"
                fill
              >
                <Tab eventKey="info" title="Account Info">
                  <AccountInfoPage userInfo={userInfo} setUserInfo={setUserInfo} />
                </Tab>
                <Tab eventKey="content" title="Content Preferences">
                  <ContentPreferences userInfo={userInfo} />
                </Tab>
              </Tabs>
            );
          }}
        </UserInfo.Consumer>
      </Container>
    </div>
  );
};

export default SettingsPage;
